import React, { useEffect, useMemo, useState, useRef } from "react";
import { FaSave, FaTimes, FaImage, FaBoxOpen } from "react-icons/fa";
import { addInventaire } from "../../../services/inventaireServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";

const DEFAULT_PHOTO = "/img/no-image.jpg";

// Helper absolu
const toAbsolute = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = import.meta.env?.VITE_API_BASE_URL || "";
  return `${base}${url}`;
};

const AddInventaireModal = ({ isOpen, onClose, refreshList }) => {
  const [formData, setFormData] = useState({
    categorie: "",
    nom: "",
    stock: "",
    note: "",
    photo_url: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [imgError, setImgError] = useState(false);
  const dialogRef = useRef(null);

  // Verrouillage scroll + focus + ESC
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const prev = root.style.overflow;
    root.style.overflow = "hidden";
    const onEsc = (e) => {
      if (e.key === "Escape" && !submitting) onClose?.();
    };
    window.addEventListener("keydown", onEsc);
    setTimeout(() => {
      dialogRef.current?.querySelector("input[name='categorie']")?.focus();
    }, 0);
    return () => {
      root.style.overflow = prev;
      window.removeEventListener("keydown", onEsc);
    };
  }, [isOpen, submitting, onClose]);

  // Reset à l’ouverture
  useEffect(() => {
    if (isOpen) {
      setFormData({
        categorie: "",
        nom: "",
        stock: "",
        note: "",
        photo_url: "",
      });
      setSubmitting(false);
      setImgError(false);
    }
  }, [isOpen]);

  const hasPhoto = useMemo(
    () => !!(formData.photo_url && formData.photo_url.trim().length > 0),
    [formData.photo_url]
  );

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose?.();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = name === "stock" ? value.replace(",", ".") : value;
    setFormData((prev) => ({ ...prev, [name]: next }));
    if (name === "photo_url") setImgError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const base = {
        categorie: formData.categorie.trim(),
        nom: formData.nom.trim(),
        photo_url:
          formData.photo_url?.trim() && !imgError ? formData.photo_url.trim() : DEFAULT_PHOTO,
        stock:
          formData.stock === "" || formData.stock === null
            ? null
            : Number.isNaN(Number(formData.stock))
            ? null
            : Math.max(0, parseInt(Number(formData.stock), 10)),
      };

      const payload = formData.note?.trim()
        ? { ...base, note: formData.note.trim() }
        : base;

      await addInventaire(payload);
      toast.success("Inventaire ajouté avec succès");
      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'inventaire", error);
      toast.error("Erreur lors de l'ajout de l'inventaire");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
      onClick={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Ajouter un article d’inventaire"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/20">
                <FaBoxOpen />
              </span>
              <h3 className="text-lg sm:text-xl font-bold uppercase">
                Ajouter un article d’inventaire
              </h3>
            </div>
            <button
              type="button"
              className="h-9 w-9 grid place-items-center rounded-xl bg-white/15 hover:bg-white/25"
              onClick={() => !submitting && onClose?.()}
              aria-label="Fermer"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
          <p className="text-white/90 text-center text-xs mt-1">
            Renseignez les informations ci-dessous.
          </p>
        </div>

        {/* Corps scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Colonne gauche */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Catégorie *
                </label>
                <input
                  type="text"
                  name="categorie"
                  value={formData.categorie}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Stock *
                  </label>
                  <input
                    type="number"
                    name="stock"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={formData.stock}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Note (optionnelle)
                  </label>
                  <input
                    type="text"
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    placeholder="État, taille, accessoires, etc."
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>
            </div>

            {/* Colonne droite : URL + Preview */}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  URL de la photo
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <FaImage />
                  </span>
                  <input
                    type="url"
                    name="photo_url"
                    placeholder="https://…/image.jpg ou /uploads/…"
                    value={formData.photo_url}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Collez une URL valide (JPG/PNG/WEBP). <strong>Astuce</strong> : vous pourrez
                  <em> téléverser</em> une image depuis la modale d’édition après création.
                </p>
              </div>

              <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3 flex items-center justify-center min-h-[180px]">
                <img
                  src={imgError || !hasPhoto ? DEFAULT_PHOTO : toAbsolute(formData.photo_url)}
                  alt="Aperçu de l’article"
                  onError={() => setImgError(true)}
                  className="max-h-44 max-w-full object-contain rounded-lg shadow-sm"
                />
              </div>
            </div>
          </div>

          {/* Espace pour le footer sticky */}
          <div className="h-2" />
        </form>

        {/* Footer sticky */}
        <div className="sticky bottom-0 z-10 px-5 sm:px-6 py-3 border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95">
          <div className="flex justify-end gap-2">
            <Button
              text="Fermer"
              icon={FaTimes}
              type="button"
              onClick={() => !submitting && onClose?.()}
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Envoi..." : "Enregistrer"}
              icon={FaSave}
              type="submit"
              className={`!rounded-xl gap-2 ${submitting ? "opacity-60 pointer-events-none" : ""}`}
              disabled={submitting}
              onClick={() => {
                // Soumettre le form programmé (il est dans le même composant)
                dialogRef.current?.querySelector("form")?.requestSubmit?.();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInventaireModal;
